import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export const DESTINATIONS_QUERY_PARAM = 'query';
export const DESTINATIONS_PAGE_PARAM = 'page';
export const DESTINATIONS_SORT_COLUMN_PARAM = 'sortColumn';
export const DESTINATIONS_SORT_ORDER_PARAM = 'sortOrder';
export const DESTINATIONS_ONLY_WITHOUT_PHOTOS_PARAM = 'onlyWithoutPhotos';
export const DESTINATIONS_HAS_GUIDE_PARAM = 'hasGuide';
export const DESTINATIONS_SOURCE_PARAM = 'source';
export const DESTINATIONS_STATUS_PARAM = 'status';
export const DESTINATIONS_GEO_TYPE_PARAM = 'geoType';
export const DESTINATIONS_HAS_CONTENT_PARAM = 'hasContent';
export const DESTINATIONS_CONTENT_TYPE_PARAM = 'contentType';

export interface DestinationsListState {
    query: string;
    page: number;
    sortColumn?: string;
    sortOrder?: string;
    onlyWithoutPhotos?: boolean;
    hasGuide?: boolean;
    source?: number;
    status?: number;
    geoType?: number;
    /** When contentType is set, content filter is active. */
    hasContent?: boolean;
    contentType?: number;
    returnToUrl?: string;
}

/** Updates for content filter: pass null to remove from URL (e.g. "All"). */
export type DestinationsListStateUpdate = Omit<
    Partial<DestinationsListState>,
    'contentType'
> & { contentType?: number | null };

export function useDestinationsListState() {
    const [searchParams, setSearchParams] = useSearchParams();

    const query = searchParams.get(DESTINATIONS_QUERY_PARAM) || '';
    const page = Number.parseInt(
        searchParams.get(DESTINATIONS_PAGE_PARAM) || '1',
        10,
    );
    const sortColumn =
        searchParams.get(DESTINATIONS_SORT_COLUMN_PARAM) || undefined;
    const sortOrder =
        searchParams.get(DESTINATIONS_SORT_ORDER_PARAM) || undefined;
    const onlyWithoutPhotos =
        searchParams.get(DESTINATIONS_ONLY_WITHOUT_PHOTOS_PARAM) === 'true';
    const hasGuide = searchParams.get(DESTINATIONS_HAS_GUIDE_PARAM) === 'true';
    const source = searchParams.get(DESTINATIONS_SOURCE_PARAM)
        ? Number.parseInt(
              searchParams.get(DESTINATIONS_SOURCE_PARAM) || '0',
              10,
          )
        : undefined;
    const status = searchParams.get(DESTINATIONS_STATUS_PARAM)
        ? Number.parseInt(
              searchParams.get(DESTINATIONS_STATUS_PARAM) || '0',
              10,
          )
        : undefined;
    const geoType = searchParams.get(DESTINATIONS_GEO_TYPE_PARAM)
        ? Number.parseInt(
              searchParams.get(DESTINATIONS_GEO_TYPE_PARAM) || '0',
              10,
          )
        : undefined;
    const hasContentParam = searchParams.get(DESTINATIONS_HAS_CONTENT_PARAM);
    const hasContent =
        hasContentParam === 'true' || hasContentParam === 'false'
            ? { true: true, false: false }[hasContentParam]
            : undefined;
    const contentTypeParam = searchParams.get(DESTINATIONS_CONTENT_TYPE_PARAM);
    const contentType =
        contentTypeParam != null && contentTypeParam !== ''
            ? Number.parseInt(contentTypeParam, 10)
            : undefined;
    const contentTypeValid =
        contentType !== undefined && !Number.isNaN(contentType)
            ? contentType
            : undefined;

    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;

    const updateState = useCallback(
        (updates: DestinationsListStateUpdate) => {
            const newParams = new URLSearchParams(searchParamsRef.current);

            if (updates.query !== undefined) {
                if (updates.query) {
                    newParams.set(DESTINATIONS_QUERY_PARAM, updates.query);
                } else {
                    newParams.delete(DESTINATIONS_QUERY_PARAM);
                }
            }

            if (updates.page !== undefined) {
                if (updates.page > 1) {
                    newParams.set(
                        DESTINATIONS_PAGE_PARAM,
                        updates.page.toString(),
                    );
                } else {
                    newParams.delete(DESTINATIONS_PAGE_PARAM);
                }
            }

            if (updates.sortColumn !== undefined) {
                if (updates.sortColumn) {
                    newParams.set(
                        DESTINATIONS_SORT_COLUMN_PARAM,
                        updates.sortColumn,
                    );
                } else {
                    newParams.delete(DESTINATIONS_SORT_COLUMN_PARAM);
                }
            }

            if (updates.sortOrder !== undefined) {
                if (updates.sortOrder) {
                    newParams.set(
                        DESTINATIONS_SORT_ORDER_PARAM,
                        updates.sortOrder,
                    );
                } else {
                    newParams.delete(DESTINATIONS_SORT_ORDER_PARAM);
                }
            }

            if (updates.onlyWithoutPhotos !== undefined) {
                if (updates.onlyWithoutPhotos) {
                    newParams.set(
                        DESTINATIONS_ONLY_WITHOUT_PHOTOS_PARAM,
                        'true',
                    );
                } else {
                    newParams.delete(DESTINATIONS_ONLY_WITHOUT_PHOTOS_PARAM);
                }
            }

            if (updates.hasGuide !== undefined) {
                if (updates.hasGuide) {
                    newParams.set(DESTINATIONS_HAS_GUIDE_PARAM, 'true');
                } else {
                    newParams.delete(DESTINATIONS_HAS_GUIDE_PARAM);
                }
            }

            if (updates.source !== undefined) {
                if (typeof updates.source === 'number') {
                    newParams.set(
                        DESTINATIONS_SOURCE_PARAM,
                        updates.source.toString(),
                    );
                } else {
                    newParams.delete(DESTINATIONS_SOURCE_PARAM);
                }
            }

            if (updates.status !== undefined) {
                if (typeof updates.status === 'number') {
                    newParams.set(
                        DESTINATIONS_STATUS_PARAM,
                        updates.status.toString(),
                    );
                } else {
                    newParams.delete(DESTINATIONS_STATUS_PARAM);
                }
            }

            if (updates.geoType !== undefined) {
                if (typeof updates.geoType === 'number') {
                    newParams.set(
                        DESTINATIONS_GEO_TYPE_PARAM,
                        updates.geoType.toString(),
                    );
                } else {
                    newParams.delete(DESTINATIONS_GEO_TYPE_PARAM);
                }
            }

            if (updates.hasContent !== undefined) {
                if (typeof updates.hasContent === 'boolean') {
                    newParams.set(
                        DESTINATIONS_HAS_CONTENT_PARAM,
                        String(updates.hasContent),
                    );
                } else {
                    newParams.delete(DESTINATIONS_HAS_CONTENT_PARAM);
                }
            }

            if (updates.contentType !== undefined) {
                if (typeof updates.contentType === 'number') {
                    newParams.set(
                        DESTINATIONS_CONTENT_TYPE_PARAM,
                        updates.contentType.toString(),
                    );
                } else {
                    newParams.delete(DESTINATIONS_CONTENT_TYPE_PARAM);
                    newParams.delete(DESTINATIONS_HAS_CONTENT_PARAM);
                }
            }

            setSearchParams(newParams, { replace: true });
        },
        [setSearchParams],
    );

    return {
        query,
        page,
        sortColumn,
        sortOrder,
        onlyWithoutPhotos,
        hasGuide,
        source,
        status,
        geoType,
        hasContent,
        contentType: contentTypeValid,
        returnToUrl: `/destinations/overview?${searchParams.toString()}`,
        updateState,
    };
}
